import { FilterValues } from "../../../data-model/filterValues";
import { OncologySubmissionFilterKeys } from "./createFilterSections";

export interface ProcessedFilterData {
  filters: { [key: string]: string[] | null };
}

/**
 * Converts a date string to YYYY-MM-DD format for the dumped_at filter.
 *
 * @input dateStr - Date in YYYYMMDD or a parseable date string
 * @return string: Date as YYYY-MM-DD, or the original string if unparseable
 */
const convertDateToDumpedAtFormat = (dateStr: string): string => {
  // Check if already in YYYYMMDD format (8 digits)
  if (/^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  // Try to parse the date
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return dateStr;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Converts default date (YYYYMMDD format) to YYYY-MM-DD format for dumped_at filter.
 *
 * @input dateStr - Date in YYYYMMDD format
 * @return string: Date as YYYY-MM-DD
 */
export const convertDefaultDateToDumpedAtFormat = (dateStr: string): string => {
  return convertDateToDumpedAtFormat(dateStr);
};

/**
 * Maps UI filter selections into the API filter payload shape.
 *
 * @input validFilters - Active filter values from Redux / the filter modal
 * @return ProcessedFilterData: Filters keyed for the oncology submission list API
 */
export const processFilterData = (
  validFilters: FilterValues[],
): ProcessedFilterData => {
  const filters: { [key: string]: string[] | null } = {};

  if (!validFilters || validFilters.length === 0) {
    return { filters };
  }

  const filter = validFilters[0];

  if (!filter || !filter.values) {
    return { filters };
  }

  Object.entries(filter.values).forEach(([filterId, filterData]) => {
    const selectedValues = filterData.values || [];

    if (selectedValues.length > 0) {
      switch (filterId) {
        case OncologySubmissionFilterKeys.ROUTE_VIA:
          filters.route_via = selectedValues;
          break;
        case OncologySubmissionFilterKeys.PA_STATUS:
          filters.response_status = selectedValues;
          break;
        case OncologySubmissionFilterKeys.PA_STATUS_BUCKET:
          filters.pa_status_reason = selectedValues;
          break;
        case OncologySubmissionFilterKeys.CMM:
          filters["meta.cmm_st_rpa_status"] = selectedValues;
          break;
        case OncologySubmissionFilterKeys.DOW:
          // Date range from filter (e.g. MM/DD/YYYY) -> YYYY-MM-DD for payload
          if (selectedValues.length >= 2) {
            filters.dumped_at = selectedValues.map(convertDateToDumpedAtFormat);
          }
          break;
        default:
          break;
      }
    }
  });

  return { filters };
};
